<template>
  <div>
    <div class="header"><span :style="centeredLabelStyle">{{ title }}</span></div>
    <template v-if="available">
      <div
        v-for="nutrient in nutrients"
        :key="nutrient.key"
        class="nutrient"
        :class="{ extended: averageActive }"
        :style="nutrientGridStyle"
      >
        <div class="name" :style="nutrientNameStyle">
          <span :style="centeredLabelStyle">{{ nutrient.name }}</span>
        </div>
        <div
          v-for="(value, dayIndex) in nutrient.value"
          :key="dayIndex"
          class="day"
          :class="nutrient.normClass[dayIndex]"
        >
          <DietDetailNutrientValue
            :difference="nutrient.differenceValue[dayIndex]"
            :measurement="nutrient.measurement"
            :norm="nutrient.norm"
            :norm-class="nutrient.normClass[dayIndex]"
            :percent="nutrient.valuePercent[dayIndex]"
            show-measurement
            :show-norm="showNormWithoutTarget || nutrient.norm > 0"
            :value="value"
            variant="week"
          >
            <template v-if="showIncompleteData" #after>
              <DietDetailNutrientIncompleteData
                class="incomplete-data"
                :data="nutrient.incompleteData[dayIndex] ?? ''"
                variant="week"
              />
            </template>
          </DietDetailNutrientValue>
        </div>
        <div class="day average" :class="nutrient.averageClass">
          <DietDetailNutrientValue
            average
            :difference="nutrient.differenceAverage"
            :measurement="nutrient.measurement"
            :norm="nutrient.norm"
            :norm-class="nutrient.averageClass"
            :percent="nutrient.averagePercent"
            show-measurement
            :show-norm="showNormWithoutTarget || nutrient.norm > 0"
            :show-positive-difference="false"
            tooltip-placement="top-end"
            :value="nutrient.average"
            variant="week"
          />
        </div>
      </div>
    </template>
    <slot v-else name="unavailable" />
  </div>
</template>

<script lang="ts" setup>
  import type { StyleValue } from 'vue'

  import type { NutrientExtended } from '@/utils/nutrients'

  import DietDetailNutrientIncompleteData from './DietDetailNutrientIncompleteData.vue'
  import DietDetailNutrientValue from './DietDetailNutrientValue.vue'

  withDefaults(
    defineProps<{
      available?: boolean
      averageActive: boolean
      centeredLabelStyle: StyleValue
      nutrientGridStyle: StyleValue
      nutrientNameStyle: StyleValue
      nutrients: NutrientExtended[]
      showIncompleteData?: boolean
      showNormWithoutTarget?: boolean
      title: string
    }>(),
    {
      available: true,
      showIncompleteData: false,
      showNormWithoutTarget: false,
    },
  )
</script>

<style scoped lang="scss">
  .header {
    height: 42px;
    font-family: Montserrat-SemiBold;
    font-size: 16px;
    line-height: 42px;
    color: #fff;
    text-align: center;
    text-transform: uppercase;
    background: var(--color-main-100);
  }

  .header span,
  .name span {
    position: sticky;
    left: 0;
    display: block;
  }

  .nutrient {
    display: grid;
    min-width: 0;
    margin: 0 4px;
  }

  .name {
    height: 32px;
    font-size: 14px;
    line-height: 32px;
    text-align: center;
    background: #f8f8f8;
  }

  .day {
    box-sizing: border-box;
    min-width: 0;
    height: 46px;
    padding: 7px 4px 7px 8px;
    margin-right: 6px;

    &.red-up,
    &.red-down {
      background: var(--color-red-10);
    }

    &.yellow-up,
    &.yellow-down {
      background: var(--color-warning-10);
    }
  }

  .average {
    display: none;
    box-sizing: border-box;
    grid-column: average;
    min-width: 0;
    margin-right: 0;
    overflow: hidden;
    background: var(--color-main-10);
  }

  .nutrient.extended .average {
    display: block;
  }

  .incomplete-data {
    margin-left: 8px;
  }
</style>
